/** Default Plaid PAYG rates, plan prices, profiles, and growth — Aug 2026 canvas seed. */

import {
  DEFAULT_GROWTH_MODEL,
  DEFAULT_GROWTH_PHASES,
} from './growthProjection';

export const SCENARIO_STORAGE_KEY = 'ffc.admin.economics.scenario.v4';
export const LEGACY_SCENARIO_STORAGE_KEY = 'ffc.admin.economics.scenario.v3';
export const LEGACY_SCENARIO_STORAGE_KEY_V2 = 'ffc.admin.economics.scenario.v2';
export const LEGACY_SCENARIO_STORAGE_KEY_V1 = 'ffc.admin.economics.scenario.v1';

export const PLAID_GROWTH_PLATFORM_FEE = 2000;

export const DEFAULT_RATES = {
  transactionsItemMo: 0.3,
  balanceCall: 0.1,
  authOnce: 1.5,
  recurringItemMo: 0.15,
  liabilitiesItemMo: 0.2,
  investmentsHoldingsItemMo: 0.18,
  investmentsTxItemMo: 0.2,
  txRefreshCall: 0.12,
  enrichPerThousand: 2.0,
  stripePercent: 0.029,
  stripeFixed: 0.3,
  // AI chat (Premium+ only). Included quota + overage packs (CI-minutes style).
  aiCostPerTurn: 0.04,
  aiIncludedTurnsPremium: 40,
  aiIncludedTurnsEnterprise: 100,
  aiPackTurns: 100,
  // Pack list price — ~2× vendor cost on overage turns ($0.08 list vs $0.04 cost).
  aiPackPrice: 8,
};

export const DEFAULT_PLANS = {
  free: {
    slug: 'free',
    name: 'Free',
    monthly: 0,
    bankLink: false,
    entitiesMax: 1,
    accountsMax: 5,
  },
  basic: {
    slug: 'basic',
    name: 'Basic',
    monthly: 9.99,
    bankLink: true,
    entitiesMax: 3,
    accountsMax: null,
  },
  premium: {
    slug: 'premium',
    name: 'Premium',
    monthly: 24.99,
    bankLink: true,
    entitiesMax: null,
    accountsMax: null,
  },
  enterprise: {
    slug: 'enterprise',
    name: 'Enterprise',
    monthly: 99.99,
    bankLink: true,
    entitiesMax: null,
    accountsMax: null,
  },
};

export const DEFAULT_PROFILES = [
  {
    id: 'light',
    name: 'Light Basic',
    plan: 'basic',
    items: 1,
    books: 1,
    balanceWaves: 8,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 0,
    addOns: {},
    note: '1 checking Item · quiet webhook sync · no AI (Basic)',
  },
  {
    id: 'typical',
    name: 'Typical Basic',
    plan: 'basic',
    items: 2,
    books: 1,
    balanceWaves: 10,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 0,
    addOns: {},
    note: 'Checking + CC · ~10 sync waves · no AI (Basic)',
  },
  {
    id: 'heavy',
    name: 'Heavy Basic',
    plan: 'basic',
    items: 5,
    books: 1,
    balanceWaves: 12,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 0,
    addOns: {},
    note: 'Multi-account · no AI (Basic)',
  },
  {
    id: 'premium-mb',
    name: 'Premium multi-book',
    plan: 'premium',
    items: 6,
    books: 2,
    balanceWaves: 12,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 30,
    addOns: {},
    note: '2 entities · included AI quota (~30 turns)',
  },
  {
    id: 'premium-ai',
    name: 'Premium AI power',
    plan: 'premium',
    items: 4,
    books: 1,
    balanceWaves: 10,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 80,
    addOns: {},
    note: 'Heavy assistant use — buys AI packs above included quota',
  },
  {
    id: 'invest',
    name: 'Investment-heavy (future)',
    plan: 'premium',
    items: 4,
    books: 1,
    balanceWaves: 10,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 20,
    addOns: { investments: true },
    note: 'If Holdings+Investments Tx enabled',
  },
  {
    id: 'power',
    name: 'Power refresher',
    plan: 'basic',
    items: 2,
    books: 1,
    balanceWaves: 45,
    txRefreshCallsMo: 0,
    authEnabled: false,
    aiTurnsMo: 0,
    addOns: {},
    note: 'Manual sync bypasses 6h Balance cooldown · no AI',
  },
];

const BASE_PROFILE_GROWTH = [
  { profileId: 'light', share0: 0.45, monthlyGrowthPct: 8 },
  { profileId: 'typical', share0: 0.3, monthlyGrowthPct: 10 },
  { profileId: 'heavy', share0: 0.08, monthlyGrowthPct: 6 },
  { profileId: 'premium-mb', share0: 0.08, monthlyGrowthPct: 12 },
  { profileId: 'premium-ai', share0: 0.04, monthlyGrowthPct: 10 },
  { profileId: 'invest', share0: 0, monthlyGrowthPct: 0 },
  { profileId: 'power', share0: 0.05, monthlyGrowthPct: 4 },
];

const BASE_GROWTH_PHASES = DEFAULT_GROWTH_PHASES.map((p) => ({ ...p }));

export const GROWTH_PRESETS = {
  bear: {
    label: 'Bear',
    startPaying: 8,
    horizonMonths: 12,
    churnPct: 2,
    model: 'monte_carlo',
    distribution: 'lognormal',
    mcRuns: 500,
    phases: BASE_GROWTH_PHASES.map((p) => ({
      ...p,
      monthlyGrowthPct: Math.max(2, p.monthlyGrowthPct - 8),
      volatilityPct: p.volatilityPct + 5,
    })),
    profileGrowth: BASE_PROFILE_GROWTH.map((row) => ({
      ...row,
      monthlyGrowthPct: Math.max(0, row.monthlyGrowthPct - 4),
    })),
  },
  base: {
    label: 'Base',
    startPaying: 8,
    horizonMonths: 12,
    churnPct: 1,
    model: 'monte_carlo',
    distribution: 'lognormal',
    mcRuns: 500,
    phases: BASE_GROWTH_PHASES.map((p) => ({ ...p })),
    profileGrowth: BASE_PROFILE_GROWTH.map((row) => ({ ...row })),
  },
  bull: {
    label: 'Bull',
    startPaying: 12,
    horizonMonths: 12,
    churnPct: 0.5,
    model: 'monte_carlo',
    distribution: 'lognormal',
    mcRuns: 800,
    phases: BASE_GROWTH_PHASES.map((p) => ({
      ...p,
      monthlyGrowthPct: p.monthlyGrowthPct + 8,
    })),
    profileGrowth: BASE_PROFILE_GROWTH.map((row) => ({
      ...row,
      monthlyGrowthPct: row.monthlyGrowthPct + 6,
      share0:
        row.profileId === 'premium-mb'
          ? 0.12
          : row.profileId === 'premium-ai'
            ? 0.08
            : row.profileId === 'light'
              ? 0.4
              : row.profileId === 'typical'
                ? 0.27
                : row.share0,
    })),
  },
};

export const DEFAULT_PLAID_MODE = 'payg';

export function createDefaultScenario() {
  return {
    version: 4,
    asOf: '2026-08-12',
    plaidMode: DEFAULT_PLAID_MODE,
    rates: { ...DEFAULT_RATES },
    plans: JSON.parse(JSON.stringify(DEFAULT_PLANS)),
    profiles: JSON.parse(JSON.stringify(DEFAULT_PROFILES)),
    growth: {
      preset: 'base',
      growthMode: 'profile',
      ...DEFAULT_GROWTH_MODEL,
      ...GROWTH_PRESETS.base,
      phases: GROWTH_PRESETS.base.phases.map((p) => ({ ...p })),
      profileGrowth: GROWTH_PRESETS.base.profileGrowth.map((row) => ({ ...row })),
    },
  };
}
